import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../../../common/widgets/common/PathGraph/PathGraph';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import PageModel from '@mm/state/model/PageModel';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import WidgetModel, { WidgetTypes } from '@mm/state/model/WidgetModel';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Heading } from '@mm/components';
import { DialogueSelectorWithReload } from '../../../common/DialogueSelectorWithReload/DialogueSelectorWithReload';
import { StartBotsDialogueEditor } from '../../../common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import { useCustomerChatWidgetPreviews } from '../use-customer-chat-widget-previews';
import './CustomerChatDialogueEditor.pcss';
import { DialogueHeaderWarningAlert } from '../../../../common/components/DialogueHeaderWarningAlert/DialogueHeaderWarningAlert';

type Props = {
  promoter: PromoterModel;
  widgets: WidgetModel[];
  page: PageModel;
};

export const CustomerChatDialogueEditor = (props: Props) => {
  const { promoter, widgets, page } = props;
  const { location } = useRouter();
  const pathGraph = useNewGraphInstance(promoter?.pageId);
  const { update: updatePromoter } = usePromoterCRUDActions();

  const chatbot = useSelector(activeChatbotSelector);
  const facebookPage = useSelector(activeFacebookPageSelector);
  const session = useSelector((state: StoreState) => state.session.session);

  const { openPreviewModal } = useCustomerChatWidgetPreviews(promoter);

  const dialogueSelectorDropdown = useMemo(() => {
    const promoterDialogueUpdate = async (dialogueId: number) => {
      if (promoter && promoter.id) {
        const updatedPromoter = {
          ...promoter,
          pageId: dialogueId,
        };
        const savedPromoter = await updatePromoter(chatbot.id, updatedPromoter);

        const newDialogueId = savedPromoter?.pageId;

        if (newDialogueId) {
          const newUrl = `${BotBuilderRoutes.customerChatEdit.getLink({
            chatbotId: chatbot.id,
            dialogueId: newDialogueId,
            promoterId: promoter.id,
          })}${location.hash || ''}`;
          return {
            newDialogueId,
            newUrl,
          };
        }
      }
    };

    return promoter?.pageId ? (
      <DialogueSelectorWithReload
        className="customer-chat-dialogue-editor__dialogue-selector"
        dialogueId={promoter.pageId}
        entityUpdateFunction={promoterDialogueUpdate}
        forFacebook
      />
    ) : null;
  }, [promoter, chatbot.id, updatePromoter]);

  const testBot = () => {
    if (!page || !facebookPage) {
      return;
    }
    window.open(
      `http://m.me/${facebookPage.remoteId}?ref=${page.searchToken}_${session.searchToken}`,
    );
  };

  return (
    <>
      <div className="customer-chat-dialogue-editor__header">
        <Heading size={3}>Choose a Dialogue for your website visitors</Heading>
        <PrimaryButton onClick={testBot} color={ButtonColor.secondaryBlue}>
          Test my bot
        </PrimaryButton>
        <DialogueHeaderWarningAlert />
      </div>

      <PathGraphContext.Provider value={pathGraph}>
        <div className="customer-chat-dialogue-editor__dialogue-selector-row">
          {dialogueSelectorDropdown}
        </div>
        <StartBotsDialogueEditor
          chatbotId={chatbot.id}
          widgets={widgets}
          platforms={['web_chat']}
          widgetsToExclude={
            chatbot.chatWidgetChannel === 'web_chat' ? [WidgetTypes.OTN] : []
          }
          dialogueId={promoter.pageId}
          showAttributesStartWarning={true}
        />
      </PathGraphContext.Provider>

      <div className="customer-chat-dialogue-editor__test-buttons-row">
        <DialogueHeaderWarningAlert />

        <PrimaryButton onClick={testBot} color={ButtonColor.secondaryBlue}>
          Test my bot
        </PrimaryButton>

        <PrimaryButton
          onClick={openPreviewModal}
          color={ButtonColor.secondaryBlue}
        >
          Live preview
        </PrimaryButton>
      </div>
    </>
  );
};
